import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import useYamlData from '../hooks/useYamlData';
import HatIcon from '../components/HatIcon';
import HatOrrery from '../components/HatOrrery';
import ThemeTimeline from '../components/ThemeTimeline';
import HatMatrix from '../components/HatMatrix';
import CultureCard from '../components/CultureCard';
import MarginCrew from '../components/MarginCrew';
import '../styles/InPractice.css';

// YAML may hand back a Date when a date is left unquoted; normalise to YYYY-MM-DD.
const isoDate = (value) =>
  typeof value === 'string' ? value : new Date(value).toISOString().slice(0, 10);

const monthLabel = (value) =>
  new Date(`${isoDate(value).slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

const InPractice = () => {
  const { data, loading, error } = useYamlData('/data/in-practice.yaml');
  const [activeHat, setActiveHat] = useState(null);
  const [selected, setSelected] = useState(null);

  // Stable identity so the memo below does not rerun on every render.
  const themes = useMemo(() => data?.themes ?? [], [data]);

  const toggleHat = (id) => setActiveHat((current) => (current === id ? null : id));
  const clearHat = () => setActiveHat(null);

  // Escape is the keyboard equivalent of clicking off a planet.
  useEffect(() => {
    if (!activeHat) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setActiveHat(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeHat]);

  /*
   * Clicking away clears the filter. Anchoring this to a container never worked
   * reliably: the orrery paints with overflow:visible, so the system you can see
   * is larger than the box that receives pointer events, and every container
   * left a dead zone somewhere. Listening at the document removes geometry from
   * the question entirely -- a click on any control does its own job, and a
   * click on inert space dismisses.
   *
   * Planets and the badge also stopPropagation, so those never reach here.
   */
  useEffect(() => {
    if (!activeHat) return undefined;
    const onClickAway = (event) => {
      if (event.target.closest?.('button, a, [role="button"], .hat-readout')) return;
      setActiveHat(null);
    };
    document.addEventListener('click', onClickAway);
    return () => document.removeEventListener('click', onClickAway);
  }, [activeHat]);

  if (loading) return <p className="tmc-state">Loading...</p>;
  if (error) return <p className="tmc-state tmc-state--error">{error}</p>;

  const { meta, bridge, hats, impact, domains, thread = [] } = data;
  const hatName = (id) => hats.find((hat) => hat.id === id)?.name ?? id;

  /*
   * The culture `offset` places away, wrapping at both ends. Walks the same
   * order the chips and the orrery use, so stepping through the badge matches
   * the list the reader can already see.
   */
  const step = (offset) => {
    const at = hats.findIndex((hat) => hat.id === activeHat);
    return hats[(at + offset + hats.length) % hats.length];
  };

  // Stated in the overlap section, derived so the copy cannot drift from the data.
  const hatCounts = themes.map((theme) => theme.hats.length);
  const fewestHats = Math.min(...hatCounts);
  const meanHats = (hatCounts.reduce((a, b) => a + b, 0) / hatCounts.length).toFixed(1);
  const widestHat = hats
    .map((hat) => ({
      name: hat.name,
      reach: new Set(
        themes.filter((theme) => theme.hats.includes(hat.id)).map((theme) => theme.domain),
      ).size,
    }))
    .sort((a, b) => b.reach - a.reach)[0];
  const matching = activeHat
    ? themes.filter((theme) => theme.hats.includes(activeHat)).length
    : themes.length;

  return (
    <div className="tmc-page page-shell in-practice">
      <header className="practice-hero">
        <p className="eyebrow">{meta.milestone} · since {monthLabel(meta.started)}</p>
        <h1>{meta.headline}</h1>
        <p className="practice-role">
          {meta.title} <span aria-hidden="true">·</span> {meta.employer}
        </p>
        <p className="tmc-lede">{meta.statement}</p>
      </header>

      <section className="practice-stats" aria-label="The year at a glance">
        <div className="practice-stat">
          <strong>{meta.pieces_of_work}</strong>
          <span>Pieces of work</span>
        </div>
        <div className="practice-stat">
          <strong>{themes.length}</strong>
          <span>Capabilities built</span>
        </div>
        <div className="practice-stat">
          <strong>{domains.length}</strong>
          <span>Domains touched</span>
        </div>
        <div className="practice-stat">
          <strong>{impact.people_developed}</strong>
          <span>People coached</span>
        </div>
      </section>

      <section className="thread" aria-labelledby="thread-heading">
        <div className="tmc-section-head">
          <p className="eyebrow">The through-line</p>
          <h2 id="thread-heading">{bridge.thesis}</h2>
          <p className="tmc-lede">
            Four employers, four industries, one habit: make the system
            understandable enough that people can move it.
          </p>
        </div>
        <ol className="thread-track">
          {thread.map((stop, index) => (
            <li
              className={`thread-stop${index === thread.length - 1 ? ' is-now' : ''}`}
              key={stop.organization}
              style={{
                '--brand': `var(--tmc-employer-${stop.key})`,
                // The connector fades this stop's colour into the next one.
                ...(thread[index + 1]
                  ? { '--next-brand': `var(--tmc-employer-${thread[index + 1].key})` }
                  : null),
              }}
            >
              <div className="thread-head">
                <p className="thread-period">
                  <span className="thread-dot" aria-hidden="true" />
                  {stop.period}
                </p>
                {/*
                  * On a white chip in both themes. Three of the four marks are
                  * dark wordmarks, so on the night canvas they would vanish, and
                  * inverting them would turn the SAS blue orange and the
                  * Manitowoc red cyan. The chip keeps every logo its own colour.
                  */}
                <span className="thread-logo">
                  <img
                    src={`${process.env.PUBLIC_URL}/logos/${stop.logo}`}
                    alt={`${stop.organization} logo`}
                    loading="lazy"
                  />
                </span>
              </div>
              <h3>{stop.label}</h3>
              <p className="thread-where">
                {stop.role} <span aria-hidden="true">·</span> {stop.organization}
              </p>
              <p className="thread-industry">{stop.industry}</p>
              <p className="thread-note">{stop.note}</p>
            </li>
          ))}
        </ol>
      </section>


      <section className="tmc-section hats-section" aria-labelledby="hats-heading">
        <div className="tmc-section-head">
          <p className="eyebrow">The hats</p>
          <h2 id="hats-heading">Nine roles, one job title</h2>
          <p className="tmc-lede">
            Most work needed several of these at once. Select a hat to follow it through
            everything below.
          </p>
        </div>
        <div className="hats-layout">
          <HatOrrery
            hats={hats}
            activeHat={activeHat}
            onToggle={toggleHat}
            onClear={clearHat}
          />

          {/* The badge is driven by the system: clicking a world opens it here. */}
          <aside
            className="hat-readout"
            aria-live="polite"
            onClick={(event) => event.stopPropagation()}
          >
            {activeHat ? (
              (() => {
                const hat = hats.find((entry) => entry.id === activeHat);
                return (
                  <div className="hat-badge is-open" style={{ '--chip': `var(--tmc-hat-${hat.id})` }}>
                    <CultureCard hat={hat} compact />
                    <p className="hat-badge-reach">
                      {matching} of {themes.length} capabilities needed it.
                    </p>
                    <div className="hat-badge-actions">
                      <button type="button" className="tmc-btn tmc-btn--sm" onClick={clearHat}>
                        Clear filter
                      </button>
                      <Link className="quiet-link" to="/cultures">
                        All nine cultures
                      </Link>

                      {/*
                        * Step through the nine without going back to the
                        * orrery. Each button names where it goes, role first
                        * and culture under it -- a control that only said
                        * "next" would make the reader click to find out.
                        */}
                      <nav className="hat-badge-step" aria-label="Other cultures">
                        {[step(-1), step(1)].map((other, i) => (
                          <button
                            key={other.id}
                            type="button"
                            className={`hat-step${i === 0 ? ' is-prev' : ' is-next'}`}
                            style={{ '--chip': `var(--tmc-hat-${other.id})` }}
                            onClick={() => setActiveHat(other.id)}
                            aria-label={`${i === 0 ? 'Previous' : 'Next'} culture: ${other.name}, ${other.culture}`}
                          >
                            <span className="hat-step-arrow" aria-hidden="true">
                              {i === 0 ? '\u2039' : '\u203a'}
                            </span>
                            <span className="hat-step-label">
                              <strong>{other.name}</strong>
                              <span>{other.culture}</span>
                            </span>
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="hat-badge">
                <p className="eyebrow">The system</p>
                <p className="hat-badge-def">
                  Nine worlds, one for each hat. Each carries its own mark, and its
                  size is how often that hat came off the peg.
                </p>
                <p className="hat-badge-reach">
                  Select a world to read what that discipline meant, and to trace it
                  through the year below.
                </p>
                <ul className="hat-chips" aria-label="Hats">
                  {hats.map((hat) => (
                    <li key={hat.id}>
                      <button
                        type="button"
                        className="hat-chip"
                        style={{ '--chip': `var(--tmc-hat-${hat.id})` }}
                        onClick={() => toggleHat(hat.id)}
                      >
                        <HatIcon hatId={hat.id} size={14} className="hat-swatch" />
                        {hat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="tmc-section chart-panel" aria-labelledby="timeline-heading">
        <MarginCrew post="timeline" />
        <div className="tmc-section-head">
          <p className="eyebrow">The year</p>
          <h2 id="timeline-heading">What got built, and when</h2>
          <p className="tmc-lede">
            Each bar is a capability, spanning the months it ran over. The trailing number is
            how many pieces of work rolled into it; a lighter bar is still in flight.
          </p>
        </div>
        <div className="timeline-layout">
          <ThemeTimeline
            themes={themes}
            domains={domains}
            spanStart={isoDate(meta.span_start)}
            spanEnd={isoDate(meta.span_end)}
            activeHat={activeHat}
            selected={selected}
            onSelect={setSelected}
          />
          <aside className="detail-panel" aria-live="polite">
            {selected ? (
              <>
                <p className="tmc-meta">
                  {selected.domain}
                  {selected.status === 'current' && <span className="in-flight-badge">In flight</span>}
                </p>
                <h3>{selected.title}</h3>
                <p>{selected.summary}</p>
                <p className="detail-span">
                  {monthLabel(selected.start)} – {monthLabel(selected.end)} · {selected.weight} pieces
                  of work
                </p>
                <ul className="tmc-tag-list">
                  {selected.hats.map((hat) => (
                    <li className="tmc-tag hat-tag" key={hat} style={{ '--chip': `var(--tmc-hat-${hat})` }}>
                      {hatName(hat)}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="detail-empty">
                <p className="tmc-meta">Detail</p>
                <p>Select any bar to read what that capability was.</p>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="tmc-section chart-panel" aria-labelledby="matrix-heading">
        <MarginCrew post="matrix" />
        <div className="tmc-section-head">
          <p className="eyebrow">The overlap</p>
          <h2 id="matrix-heading">Nine hats, rarely worn alone</h2>
          <p className="tmc-lede">
            Not one capability was single-discipline. The smallest needed {fewestHats};
            the average needed {meanHats}. {widestHat.name} turned up in{' '}
            {widestHat.reach} of the {domains.length} domains, the connective tissue
            between all of it. Each dot is sized by how much work that pairing took.
          </p>
        </div>
        <HatMatrix
          themes={themes}
          domains={domains}
          hats={hats}
          activeHat={activeHat}
          onToggle={toggleHat}
        />
      </section>

      <section className="tmc-section" aria-labelledby="impact-heading">
        <div className="tmc-section-head">
          <p className="eyebrow">Enablement</p>
          <h2 id="impact-heading">The part that outlasts the platform</h2>
          <p className="tmc-lede">
            Systems get replaced. The people who learned to build them do not. Figures below are
            aggregates. No individual is named or assessed.
          </p>
        </div>
        <div className="tmc-card-grid">
          <article className="tmc-card tmc-card--center">
            <h3>{impact.deep_apprenticeships}</h3>
            <p>Deep apprenticeships, zero to practitioner</p>
          </article>
          <article className="tmc-card tmc-card--center">
            <h3>{impact.peers_elevated}</h3>
            <p>Peers elevated into co-builders</p>
          </article>
          <article className="tmc-card tmc-card--center">
            <h3>{impact.curricula_authored}</h3>
            <p>Reusable curricula authored</p>
          </article>
          <article className="tmc-card tmc-card--center">
            <h3>{impact.sessions}</h3>
            <p>Teaching sessions across {impact.topics_taught} topics</p>
          </article>
        </div>
      </section>

      <nav className="practice-links" aria-label="Related pages">
        <Link to="/career">
          <span>01</span>
          <strong>The career atlas</strong>
          <ArrowUpRight aria-hidden="true" />
        </Link>
        <Link to="/resume">
          <span>02</span>
          <strong>Full resume</strong>
          <ArrowUpRight aria-hidden="true" />
        </Link>
      </nav>
    </div>
  );
};

export default InPractice;
